/** Local in-topic search — title + body, multi-word + light fuzzy match. */

export function stripHtml(html = "") {
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/\s+/g, " ")
    .trim();
}

function collectEditorJsStrings(data, out = []) {
  if (!data || typeof data !== "object") return out;
  if (typeof data.text === "string") out.push(stripHtml(data.text));
  if (typeof data.content === "string") out.push(stripHtml(data.content));
  if (typeof data.caption === "string") out.push(stripHtml(data.caption));
  if (Array.isArray(data.items)) {
    data.items.forEach((it) => {
      if (typeof it === "string") out.push(stripHtml(it));
      else if (it && typeof it === "object") collectEditorJsStrings(it, out);
    });
  }
  if (Array.isArray(data.content)) {
    data.content.forEach((row) => {
      if (Array.isArray(row)) row.forEach((cell) => out.push(stripHtml(String(cell))));
    });
  }
  if (data.meta && typeof data.meta === "object") {
    if (data.meta.title) out.push(stripHtml(data.meta.title));
    if (data.meta.description) out.push(stripHtml(data.meta.description));
  }
  return out;
}

export function editorJsPlainText(content) {
  if (!content) return "";
  if (typeof content === "string") {
    const trimmed = content.trim();
    if (!trimmed.startsWith("{")) return stripHtml(content);
    try {
      const data = JSON.parse(trimmed);
      if (!data?.blocks) return stripHtml(content);
      const parts = [];
      data.blocks.forEach((block) => collectEditorJsStrings(block?.data, parts));
      return parts.filter(Boolean).join(" ");
    } catch {
      return stripHtml(content);
    }
  }
  if (content?.blocks) {
    const parts = [];
    content.blocks.forEach((block) => collectEditorJsStrings(block?.data, parts));
    return parts.filter(Boolean).join(" ");
  }
  return "";
}

export function getArticleSearchBlob(article) {
  return [
    article?.title || "",
    article?.excerpt || "",
    editorJsPlainText(article?.content),
  ].join(" ").toLowerCase();
}

export function parseSearchTerms(query) {
  return (query || "")
    .trim()
    .split(/\s+/)
    .map((t) => t.trim())
    .filter((t) => t.length >= 1);
}

function levenshtein(a, b) {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const row = Array.from({ length: b.length + 1 }, (_, i) => i);
  for (let i = 1; i <= a.length; i += 1) {
    let prev = i;
    for (let j = 1; j <= b.length; j += 1) {
      const val = a[i - 1] === b[j - 1] ? row[j - 1] : Math.min(row[j - 1], row[j], prev) + 1;
      row[j - 1] = prev;
      prev = val;
    }
    row[b.length] = prev;
  }
  return row[b.length];
}

export function termMatches(term, haystack) {
  const t = term.toLowerCase();
  const h = haystack.toLowerCase();
  if (!t || !h) return false;
  if (h.includes(t)) return true;
  if (t.length >= 4) {
    const words = h.split(/[\s,.;:!?،؛]+/).filter(Boolean);
    return words.some((w) => {
      if (w.includes(t) || t.includes(w)) return true;
      return levenshtein(w, t) <= 1;
    });
  }
  return false;
}

export function articleMatchesSearch(article, terms) {
  if (!terms.length) return true;
  const blob = getArticleSearchBlob(article);
  return terms.some((term) => termMatches(term, blob));
}

export function articleSearchScore(article, terms) {
  const blob = getArticleSearchBlob(article);
  const title = (article?.title || "").toLowerCase();
  let score = 0;
  terms.forEach((term) => {
    const tl = term.toLowerCase();
    if (title.includes(tl)) score += 10;
    else if (termMatches(term, title)) score += 6;
    if (termMatches(term, blob)) score += 2;
  });
  return score;
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/** Highlight all terms in plain text (returns HTML string). */
export function highlightTermsHtml(text, terms) {
  if (!text || !terms?.length) return text || "";
  let out = String(text);
  const sorted = [...terms].sort((a, b) => b.length - a.length);
  sorted.forEach((term) => {
    if (!term) return;
    const re = new RegExp(`(${escapeRegExp(term)})`, "gi");
    out = out.replace(re, '<mark class="algolia-hit">$1</mark>');
  });
  return out;
}

/** Highlight terms in HTML-ish EditorJS inline text (preserves simple tags). */
export function highlightInlineHtml(text, terms) {
  if (!text || !terms?.length) return text || "";
  return highlightTermsHtml(text, terms);
}

export function filterTopicArticles(articles, query) {
  const terms = parseSearchTerms(query);
  if (!terms.length) {
    return (articles || []).map((a) => ({ article: a, terms: [], score: 0 }));
  }
  return (articles || [])
    .filter((a) => articleMatchesSearch(a, terms))
    .map((a) => ({ article: a, terms, score: articleSearchScore(a, terms) }))
    .sort((a, b) => b.score - a.score);
}
